import type {
  ContinuationCache,
  ListViewSession,
  OperationSession,
} from './common.model';
import type {
  AddVehicleOrdersResult,
  VehicleOrderDetailsApiResponse,
  VehicleOrderDetailsTableColumns,
  VehicleOrderStatus,
  VehicleOrderSummary,
} from './vehicleOrders.model';

export type PageTokens = {
  currentToken: string;
  previousToken: string;
  nextToken: string;
  firstPageItemIndex: number;
  totalItems: number;
};

export interface SelectedVehicleOrderCodes {
  [orderNumber: string]: number;
}

export interface VehicleOrderListSession
  extends ListViewSession,
    OperationSession {
  selectedVehicleOrderCodes: SelectedVehicleOrderCodes;
  filterModalOpen?: boolean;
  vehicleOrders: VehicleOrderDetailsApiResponse[];
  searchCriteria: string | undefined;
  /**
   * Routes vehicle order list only. Synced from org default when the list loads;
   * updated by the filter toggle thereafter.
   */
  includeAutoImportedOrders?: boolean;
  filterCriteria: {
    status?: VehicleOrderStatus[];
    year?: string[];
    make?: string[];
  };
  sortOrder: {
    name: keyof Partial<VehicleOrderDetailsTableColumns>;
    direction: 'asc' | 'desc';
  };
  pageTokens: PageTokens;
  continuationCache?: ContinuationCache<VehicleOrderDetailsApiResponse[]>;
  allSelected?: boolean;
  unselectedOrderCodes?: string[];
  /** Order list table column visibility, keyed by stable column id (accessorKey/id). */
  columnVisibilityById?: Record<string, boolean>;
  /** @deprecated Legacy header-keyed visibility; read-only for in-session migration. */
  columnVisibilityByHeader?: Record<string, boolean>;
  /** Order list table column order by stable column id (accessorKey/id). */
  columnOrder?: string[];
}
export interface VehicleOrderDetailsSession extends OperationSession {
  details?: VehicleOrderDetailsApiResponse;
}
export interface VehicleOrderAddSession extends OperationSession {
  order?: AddVehicleOrdersResult;
  isAddOrder?: boolean;
}
export interface VehicleOrderDashboardSession extends OperationSession {
  summary?: VehicleOrderSummary;
}
export interface VehicleOrderRemoveSession extends OperationSession {
  orders?: string[];
}
export interface VehicleRecentOrderDashboardSession extends OperationSession {
  recentOrders?: VehicleOrderDetailsApiResponse[];
}

export enum VehicleOrderSessionViewType {
  VEHICLE_ORDER_LIST = 'Vehicle Order List',
  VEHICLE_ORDER_DETAILS = 'Vehicle Order Details',
  VEHICLE_ORDER_ADD = 'Vehicle Order Adds',
  VEHICLE_ORDER_LIST_EXPORT = 'Vehicle Order List Export',
  VEHICLE_ORDER_DASHBOARD = 'Vehicle Order Dashboard',
  VEHICLE_RECENT_ORDER_DASHBOARD = 'Vehicle Recent Order Dashboard',
  VEHICLE_ORDER_REMOVE = 'Remove Vehicle Orders',
}

export type VehicleOrderSessionConfig = {
  [VehicleOrderSessionViewType.VEHICLE_ORDER_LIST]?: {
    [key: string]: Partial<VehicleOrderListSession>;
  };
  [VehicleOrderSessionViewType.VEHICLE_ORDER_DETAILS]?: {
    [key: string]: Partial<VehicleOrderDetailsSession>;
  };
  [VehicleOrderSessionViewType.VEHICLE_ORDER_ADD]?: {
    [key: string]: Partial<VehicleOrderAddSession>;
  };
  [VehicleOrderSessionViewType.VEHICLE_ORDER_LIST_EXPORT]?: {
    [key: string]: Partial<OperationSession>;
  };
  [VehicleOrderSessionViewType.VEHICLE_ORDER_DASHBOARD]?: {
    [key: string]: Partial<VehicleOrderDashboardSession>;
  };
  [VehicleOrderSessionViewType.VEHICLE_RECENT_ORDER_DASHBOARD]?: {
    [key: string]: Partial<VehicleRecentOrderDashboardSession>;
  };
  [VehicleOrderSessionViewType.VEHICLE_ORDER_REMOVE]?: {
    [key: string]: Partial<VehicleOrderRemoveSession>;
  };
};
export interface VehicleOrdersState {
  sessionConfigs: VehicleOrderSessionConfig;
}
